import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { AccessTime as AccessTimeIcon, ArrowBack as ArrowBackIcon, Edit as EditIcon } from '@mui/icons-material';
import { GenderAgeDisplay, NameNicknameDisplay, TimeDisplay, TruncatedText } from '../components/common';
import TalkPlanMasterEditDialog from '../components/talkPlanMaster/TalkPlanMasterEditDialog';
import { getTalkPlanMasterDetail } from '../api/talkPlanMasters';
import { TalkPlanMasterDetailResponse } from '../types/talkPlanMaster';
import { NURSE_TYPE_THEME, ROLE_COLORS, ROLE_TYPES } from '../types/nurse';
import { getHumanReadableTimeAgo } from '../utils/dateUtils';

const TEXT_MAX_LENGTH = 50;
const TalkPlanMasterDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [ talkPlanMaster, setTalkPlanMaster ] = useState<TalkPlanMasterDetailResponse | null>(null);
  const [ page, setPage ] = useState(0);
  const [ rowsPerPage, setRowsPerPage ] = useState(20);
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);
  const [ openEditDialog, setOpenEditDialog ] = useState(false);
  const fetchTalkPlanMasterDetail = async() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const planId = parseInt(id, 10);
      if (isNaN(planId)) {
        throw new Error('無効な対話プランマスターIDです');
      }
      const params = {
        nurses_page: page + 1, // APIは1-indexed
        nurses_page_size: rowsPerPage,
      };
      const data = await getTalkPlanMasterDetail(planId, params);
      setTalkPlanMaster(data);
    } catch (err) {
      console.error('対話プランマスター詳細の取得に失敗しました', err);
      setError('対話プランマスター詳細の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTalkPlanMasterDetail();
  }, [ id, page, rowsPerPage ]);
  const handleBack = () => {
    navigate('/talk_plan_masters');
  };
  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleOpenEditDialog = () => {
    setOpenEditDialog(true);
  };
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };
  const handleEditSuccess = () => {
    // 編集成功後、データを再取得
    fetchTalkPlanMasterDetail();
    handleCloseEditDialog();
  };
  const getStatusChip = () => {
    if (!talkPlanMaster) return null;
    const chipStyle = {
      fontWeight: 'bold',
      minWidth: '80px',
      maxWidth: '100%',
    };
    if (talkPlanMaster.discarded_at) {
      return <Chip label="削除済み" size="small" color="error" sx={chipStyle}/>;
    }
    if (talkPlanMaster.published) {
      return <Chip label="公開中" size="small" color="success" sx={chipStyle}/>;
    }
    return <Chip label="非公開" size="small" color="default" sx={chipStyle}/>;
  };
  // ロール名を日本語に変換
  const getRoleLabel = (roleType: string) => {
    return ROLE_TYPES[roleType as keyof typeof ROLE_TYPES] || roleType;
  };
  // 看護師行クリックハンドラ
  const handleNurseRowClick = (nurseId: number, event: React.MouseEvent) => {
    // 特定の子要素がクリックされた場合はイベント伝播をスキップ
    if ((event.target as HTMLElement).closest('.no-propagation')) {
      return;
    }
    navigate(`/nurses/${nurseId}`);
  };
  if (loading) {
    return (
      <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <LinearProgress sx={{ width: '100%', mb: 2 }}/>
        <Typography variant="body1">対話プランマスターデータを読み込み中...</Typography>
      </Box>
    );
  }
  if (error || !talkPlanMaster) {
    return (
      <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Alert severity="error">{error || '対話プランマスターデータの読み込みに失敗しました。'}</Alert>
        <Button startIcon={<ArrowBackIcon/>} sx={{ mt: 2 }} onClick={handleBack}>
          一覧に戻る
        </Button>
      </Box>
    );
  }
  return (
    <Box>
      {/* 戻るボタン */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        <Button onClick={handleBack} startIcon={<ArrowBackIcon/>} variant="outlined" color="primary">
          一覧に戻る
        </Button>
      </Box>

      {/* 対話プランマスター基本情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {getStatusChip()}
              <Typography variant="h5" component="h1">
                {talkPlanMaster.name || '（名称未設定）'}
              </Typography>
            </Box>
            <IconButton
              size="medium"
              onClick={handleOpenEditDialog}
              sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  color: 'primary.main',
                }
              }}
            >
              <EditIcon/>
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={2}>
              <Typography variant="subtitle2" color="text.secondary">
                価格
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                ¥{talkPlanMaster.price.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Typography variant="subtitle2" color="text.secondary">
                期間
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {talkPlanMaster.period_days}日間
              </Typography>
            </Grid>
            <Grid item xs={12} sm={5} md={8}>
              <Typography variant="subtitle2" color="text.secondary">
                返信頻度
              </Typography>
              <Typography variant="body1">
                {talkPlanMaster.reply_frequency || '-'}
              </Typography>
            </Grid>
            {talkPlanMaster.description && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  説明
                </Typography>
                <Typography variant="body1">
                  <TruncatedText text={talkPlanMaster.description} maxLength={TEXT_MAX_LENGTH}/>
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                備考
              </Typography>
              <Typography variant="body1">
                <TruncatedText text={talkPlanMaster.remarks} maxLength={TEXT_MAX_LENGTH}/>
              </Typography>
            </Grid>
            {/* 作成日時・更新日時 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    作成日時
                  </Typography>
                  <Typography variant="body2">
                    <TimeDisplay timestamp={talkPlanMaster.created_at}/>
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    更新日時
                  </Typography>
                  <Typography variant="body2">
                    <TimeDisplay timestamp={talkPlanMaster.updated_at}/>
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 看護師一覧 */}
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
        登録看護師一覧 （
        <span style={{ fontWeight: 'bold', fontSize: '1.3em' }}>{talkPlanMaster.nurses_count}</span> 名）
      </Typography>
      {talkPlanMaster.nurses.length === 0 && !loading ? (
        <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">まだ登録している看護師がいません</Typography>
        </Paper>
      ) : (
        <Paper elevation={2}>
          <TableContainer>
            <Table stickyHeader aria-label="看護師一覧">
              <TableHead>
                <TableRow>
                  {/* ロール列 */}
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                      borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                    }}
                  >
                    ロール
                  </TableCell>

                  {/* 名前/ニックネーム列 */}
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                      borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                    }}
                  >
                    名前 / ニックネーム
                  </TableCell>

                  {/* 性別（年齢）列 */}
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: '130px',
                      backgroundColor: NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                      borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`,
                      textAlign: 'left'
                    }}
                  >
                    性別 (年齢)
                  </TableCell>

                  {/* 受付状態列 */}
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                      borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                    }}
                  >
                    受付状態
                  </TableCell>

                  {/* プラン登録日時列 */}
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                      borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`,
                      minWidth: '150px'
                    }}
                  >
                    プラン登録日時
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {talkPlanMaster.nurses.map((nurse) => (
                  <TableRow
                    key={nurse.id}
                    hover
                    onClick={(event) => handleNurseRowClick(nurse.id, event)}
                    sx={theme => ({
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      ...(nurse.resigned_at ? {
                        backgroundColor: '#fcf5f5',
                        boxShadow: 'inset 0 0 5px rgba(220, 220, 220, 0.5)',
                        opacity: 0.8,
                        '& td': { color: '#777' },
                        borderLeft: '3px solid #ffcccc',
                      } : {})
                    })}
                  >
                    {/* ロール */}
                    <TableCell>
                      <Chip
                        label={getRoleLabel(nurse.role_type)}
                        size="small"
                        style={{
                          backgroundColor: ROLE_COLORS[nurse.role_type as keyof typeof ROLE_COLORS] || '#757575',
                          color: 'white',
                          fontWeight: 'bold',
                          opacity: nurse.resigned_at ? 0.6 : 1
                        }}
                      />
                    </TableCell>

                    {/* 名前・ニックネーム */}
                    <TableCell>
                      <NameNicknameDisplay
                        name={nurse.name}
                        nickname={nurse.nickname}
                        isTestAccount={nurse.is_test_account}
                        nameColor={nurse.resigned_at ? '#666' : undefined}
                        nicknameColor={nurse.resigned_at ? '#888' : 'text.secondary'}
                      />
                    </TableCell>

                    {/* 性別・年齢 */}
                    <TableCell align="left" sx={{ textAlign: 'left' }}>
                      <GenderAgeDisplay genderType={nurse.gender_type} birthday={nurse.birthday}/>
                    </TableCell>

                    {/* 受付状態 */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography
                          component="span"
                          sx={{
                            color: nurse.can_assign ? '#2e7d32' : '#c62828',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            mr: 0.5,
                            ...(nurse.resigned_at ? { opacity: 0.6 } : {})
                          }}
                        >
                          {nurse.can_assign ? '○' : '×'}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: nurse.can_assign ? '#2e7d32' : '#c62828',
                            fontSize: '0.9rem',
                            ...(nurse.resigned_at ? { opacity: 0.6 } : {})
                          }}
                        >
                          {nurse.can_assign ? '受付中' : '停止中'}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* プラン登録日時 */}
                    <TableCell>
                      {nurse.talk_plan_updated_at && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {getHumanReadableTimeAgo(nurse.talk_plan_updated_at)}
                          </Typography>
                          <AccessTimeIcon fontSize="small" color="action" sx={{ fontSize: '0.9rem', opacity: 0.7 }}/>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[ 10, 20, 50 ]}
            component="div"
            count={talkPlanMaster.nurses_count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) =>
              `${count}件中 ${from}-${to}件`
            }
          />
        </Paper>
      )}

      {talkPlanMaster && (
        <TalkPlanMasterEditDialog
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          talkPlanMaster={talkPlanMaster}
          onSuccess={handleEditSuccess}
        />
      )}
    </Box>
  );
};
export default TalkPlanMasterDetail;
